import React, { useState, useEffect } from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { CODE_COUNTRY_USER_PRF } from "../../constants/constant";
import styles from "./DashboardConfigModal.module.css";

const DashboardConfigModal = ({ isOpen, onClose, onConfirm, initialCountries = [] }) => {
  const [selectedCountries, setSelectedCountries] = useState([]);
  
  // Get all country names from the constant
  const countryNames = Object.keys(CODE_COUNTRY_USER_PRF);

  useEffect(() => {
    if (isOpen) {
      // When modal opens, set the selected countries from initialCountries
      setSelectedCountries(initialCountries);
    }
  }, [isOpen, initialCountries]);

  const handleCountryChange = (event) => {
    const value = event.target.value;
    setSelectedCountries(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemoveCountry = (countryToRemove) => {
    setSelectedCountries(selectedCountries.filter((country) => country !== countryToRemove));
  };

  const handleClearAll = () => {
    setSelectedCountries([]);
  };

  const handleConfirm = () => {
    // Convert country names to codes
    const countryCodes = selectedCountries.map(
      (countryName) => CODE_COUNTRY_USER_PRF[countryName]
    );
    onConfirm(countryCodes);
    onClose();
  };

  const handleCancel = () => {
    setSelectedCountries(initialCountries);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Configure Dashboard</h2>
          <button className={styles.closeButton} onClick={handleCancel}>
            <CloseIcon />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.modalBody}>
          {/* Multi-select Dropdown */}
          <FormControl fullWidth>
            <Select
              multiple
              displayEmpty
              value={selectedCountries}
              onChange={handleCountryChange}
              input={<OutlinedInput />}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <span style={{ color: "#999" }}>Select countries</span>;
                }
                return `${selected.length} countries selected`;
              }}
              MenuProps={{
                PaperProps: {
                  style: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              {countryNames.map((country) => (
                <MenuItem key={country} value={country}>
                  <Checkbox checked={selectedCountries.indexOf(country) > -1} />
                  <ListItemText primary={country} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Selected Countries Chips */}
          {selectedCountries.length > 0 && (
            <>
              <div className={styles.selectedCountries}>
                {selectedCountries.map((country) => (
                  <div key={country} className={styles.countryChip}>
                    <span>{country}</span>
                    <button
                      className={styles.chipRemoveButton}
                      onClick={() => handleRemoveCountry(country)}
                      aria-label={`Remove ${country}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <button className={styles.clearAllButton} onClick={handleClearAll}>
                Clear All
              </button>
            </>
          )}
        </div>

        {/* Modal Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.cancelButton} onClick={handleCancel}>
            Cancel
          </button>
          <button
            className={styles.confirmButton}
            onClick={handleConfirm}
            disabled={selectedCountries.length === 0}
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default DashboardConfigModal;
